/**
 * useAdminStudents - Hook for fetching admin student data with server-side filtering
 *
 * This hook provides:
 * - Server-side filtering, sorting, and pagination via RPC
 * - Manages all filter/sort/pagination state internally
 * - Debounced search
 * - Loading and error states
 * - Persistence of preferences to localStorage
 * - Request cancellation for rapid filter changes
 * - Response caching with TTL
 */

import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { ApplicationStatus } from '@/types/application';
import { EnhancedErrorLogger } from '@/utils/error-tracking/enhanced-logger';
import {
  GetAdminStudentsParams,
  AdminStudentRow,
  TransformedAdminStudent,
  transformAdminStudentRow,
  AdminSearchType,
  ENROLLED_STATUSES,
  APPLICATION_STATUSES,
  DEFAULT_ADMIN_PAGE_SIZE,
  FlagFilter,
} from '@/types/admin-students-rpc';
import { toError } from '@/utils/to-error';

// ============================================================================
// Response Cache
// ============================================================================

interface CacheEntry {
  data: TransformedAdminStudent[];
  totalCount: number;
  timestamp: number;
}

// Cache TTL in milliseconds (30 seconds) - data is "fresh" for this long
const CACHE_TTL = 30 * 1000;
// Stale TTL - data can be served stale for this long while revalidating (5 minutes)
const STALE_TTL = 5 * 60 * 1000;
// Max cache entries to prevent memory bloat
const MAX_CACHE_ENTRIES = 50;
// Background refresh interval (2 minutes)
const BACKGROUND_REFRESH_INTERVAL = 2 * 60 * 1000;

// In-memory cache for RPC responses
const responseCache = new Map<string, CacheEntry>();

// Separate small cache for the status-count aggregate. That RPC ignores
// pagination by design, so it scans the whole cohort matching the current
// search and filters - the single most expensive query this hook issues, and
// until now the only one with no cache at all. It shares CACHE_TTL so the
// counts can never be staler than the page they label.
const statusCountCache = new Map<string, { counts: Record<string, number>; timestamp: number }>();
const MAX_STATUS_COUNT_ENTRIES = 30;

const getCachedStatusCounts = (key: string): Record<string, number> | null => {
  const hit = statusCountCache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.timestamp > CACHE_TTL) {
    statusCountCache.delete(key);
    return null;
  }
  return hit.counts;
};

const clearStatusCountCache = () => statusCountCache.clear();

const setCachedStatusCounts = (key: string, counts: Record<string, number>) => {
  // Re-insert so the Map's iteration order reflects recency, making the
  // eviction below least-recently-USED rather than merely oldest-inserted.
  statusCountCache.delete(key);
  statusCountCache.set(key, { counts, timestamp: Date.now() });
  while (statusCountCache.size > MAX_STATUS_COUNT_ENTRIES) {
    const oldest = statusCountCache.keys().next().value;
    if (oldest === undefined) break;
    statusCountCache.delete(oldest);
  }
};

/**
 * Create a stable cache key from RPC params
 */
function createCacheKey(params: GetAdminStudentsParams): string {
  // Sort object keys for consistent serialization
  return JSON.stringify(params, Object.keys(params).sort());
}

/**
 * Cache status for stale-while-revalidate
 */
type CacheStatus = 'fresh' | 'stale' | 'expired';

/**
 * Get cached response with staleness info
 */
function getCachedResponse(key: string): { entry: CacheEntry; status: CacheStatus } | null {
  const entry = responseCache.get(key);
  if (!entry) return null;

  const age = Date.now() - entry.timestamp;

  // Check if completely expired (beyond stale TTL)
  if (age > STALE_TTL) {
    responseCache.delete(key);
    return null;
  }

  // Check if fresh or stale
  const status: CacheStatus = age <= CACHE_TTL ? 'fresh' : 'stale';
  return { entry, status };
}

/**
 * Store response in cache
 */
function setCachedResponse(key: string, data: TransformedAdminStudent[], totalCount: number): void {
  // Evict oldest entries if cache is full
  if (responseCache.size >= MAX_CACHE_ENTRIES) {
    const oldestKey = responseCache.keys().next().value;
    if (oldestKey) {
      responseCache.delete(oldestKey);
    }
  }

  responseCache.set(key, {
    data,
    totalCount,
    timestamp: Date.now(),
  });
}

/**
 * Clear the entire cache (useful for manual refresh or logout)
 *
 * Clears the status-count cache too. Both are module-level and survive an SPA
 * logout, so leaving the counts behind meant the next admin to sign in on the
 * same tab could be shown the previous admin's counts for up to CACHE_TTL.
 * Folded in here rather than wired separately into AuthContext so there is only
 * one place to remember.
 */
function clearCache(): void {
  responseCache.clear();
  clearStatusCountCache();
}

// Export for use in AuthContext logout
export { clearCache as clearAdminStudentsCache };

// Equality helpers for the filter state below.
//
// The consumer component keeps its own copy of every filter and mirrors it in
// here through a stack of effects. Each of those pushed a freshly-constructed
// Set, so the identity always differed from this hook's own `new Set()` even
// when the CONTENTS were identical - and the auto-fetch effect keys on those
// identities. Result: opening the tab issued a second full round of RPCs, and
// `setSelectedStatuses(new Set())` on a tab change forced another.
function setsEqual<T>(a: Set<T>, b: Set<T>): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

function dateRangesEqual(
  a: { start: Date | null; end: Date | null },
  b: { start: Date | null; end: Date | null }
): boolean {
  if (a === b) return true;
  const t = (d: Date | null) => (d ? d.getTime() : null);
  return t(a.start) === t(b.start) && t(a.end) === t(b.end);
}

/**
 * useState whose setter bails out when the new value is EQUIVALENT to the old
 * one, preserving the previous reference. Accepts a value or an updater, like
 * useState. Returning `prev` from the updater makes React skip the re-render,
 * so dependent effects keyed on identity do not re-fire.
 */
function useStableState<T>(
  initial: T,
  equal: (a: T, b: T) => boolean
): [T, (next: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(initial);
  const equalRef = useRef(equal);
  equalRef.current = equal;
  const set = useCallback((next: T | ((prev: T) => T)) => {
    setValue((prev) => {
      const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
      return equalRef.current(prev, resolved) ? prev : resolved;
    });
  }, []);
  return [value, set];
}

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export interface UseAdminStudentsOptions {
  schoolId: string | null; // 'all' for all schools, or specific school ID
  academicYear: string;
  activeTab: 'all' | 'new'; // 'all' = enrolled students, 'new' = applications
  enabled?: boolean;
  initialPageSize?: number;
}

export interface UseAdminStudentsReturn {
  // Data
  students: TransformedAdminStudent[];
  totalCount: number;
  /** Cohort-wide per-status counts (server-side, filter-aware, ignores the status filter). */
  statusCounts: Record<string, number>;

  // Loading states
  isLoading: boolean;
  isRefreshing: boolean;
  isBackgroundRefreshing: boolean;
  error: string | null;

  // Search
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchType: AdminSearchType;
  setSearchType: (type: AdminSearchType) => void;

  // Status filter
  selectedStatuses: Set<ApplicationStatus>;
  setSelectedStatuses: (statuses: Set<ApplicationStatus>) => void;
  toggleStatus: (status: ApplicationStatus) => void;
  clearStatusFilters: () => void;

  // Schools filter (for "All Schools" mode)
  selectedSchools: Set<string>;
  setSelectedSchools: (schools: Set<string>) => void;
  toggleSchool: (schoolId: string) => void;
  clearSchoolFilters: () => void;

  // Grade filter
  selectedGrades: Set<string>;
  setSelectedGrades: (grades: Set<string>) => void;
  toggleGrade: (grade: string) => void;
  clearGradeFilters: () => void;

  // City filter
  selectedCities: Set<string>;
  setSelectedCities: (cities: Set<string>) => void;
  toggleCity: (city: string) => void;
  clearCityFilters: () => void;

  // School choices filter
  selectedSchoolChoices: Set<string>;
  setSelectedSchoolChoices: (choices: Set<string>) => void;
  toggleSchoolChoice: (schoolId: string) => void;
  clearSchoolChoiceFilters: () => void;

  // Date range filter
  dateRange: { start: Date | null; end: Date | null };
  setDateRange: (range: { start: Date | null; end: Date | null }) => void;
  clearDateRange: () => void;

  // Flags filter
  selectedFlags: Set<FlagFilter>;
  setSelectedFlags: (flags: Set<FlagFilter>) => void;
  toggleFlag: (flag: FlagFilter) => void;
  clearFlagFilters: () => void;

  // Sorting
  sortField: string;
  setSortField: (field: string) => void;
  sortDirection: 'asc' | 'desc';
  setSortDirection: (direction: 'asc' | 'desc') => void;
  handleSort: (field: string) => void;

  // Pagination
  currentPage: number;
  setCurrentPage: (page: number) => void;
  itemsPerPage: number;
  setItemsPerPage: (count: number) => void;
  totalPages: number;

  // Actions
  refresh: () => Promise<void>;
  fetchStudents: () => Promise<void>;
  clearAllFilters: () => void;
  clearCache: () => void;

  // Export
  isExporting: boolean;
  exportProgress: { current: number; total: number };
  exportAllStudents: (onProgress?: (current: number, total: number) => void) => Promise<TransformedAdminStudent[]>;

  // Filter counts
  activeFilterCount: number;
}

export function useAdminStudents({
  schoolId,
  academicYear,
  activeTab,
  enabled = true,
  initialPageSize = DEFAULT_ADMIN_PAGE_SIZE,
}: UseAdminStudentsOptions): UseAdminStudentsReturn {
  // Data state
  const [students, setStudents] = useState<TransformedAdminStudent[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  // Bumped by refresh(). The count effect watches only filter inputs, so a bulk
  // withdraw or delete never re-triggered it and the status counts kept showing
  // pre-mutation numbers. Pre-dates the count cache; fixed here because the
  // cache would otherwise extend the stale window.
  const [countRefreshToken, setCountRefreshToken] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0 });

  // Background refresh state
  const [isBackgroundRefreshing, setIsBackgroundRefreshing] = useState(false);

  // Search state with debouncing
  const [searchQuery, setSearchQuery] = useState('');
  // 300ms lost to real typing cadence: measured against production telemetry,
  // 483 of 822 committed search terms were a strict prefix of a longer term
  // typed in the same session, i.e. users pause mid-word for longer than the
  // window and each pause cost a full RPC fan-out.
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const [searchType, setSearchType] = useState<AdminSearchType>('all');

  // Status filter state
  const [selectedStatuses, setSelectedStatuses] = useStableState<Set<ApplicationStatus>>(new Set(), setsEqual);

  // Schools filter state (for "All Schools" mode)
  const [selectedSchools, setSelectedSchools] = useStableState<Set<string>>(new Set(), setsEqual);

  // Grade filter state
  const [selectedGrades, setSelectedGrades] = useStableState<Set<string>>(new Set(), setsEqual);

  // City filter state
  const [selectedCities, setSelectedCities] = useStableState<Set<string>>(new Set(), setsEqual);

  // School choices filter state
  const [selectedSchoolChoices, setSelectedSchoolChoices] = useStableState<Set<string>>(new Set(), setsEqual);

  // Date range filter state
  const [dateRange, setDateRange] = useStableState<{ start: Date | null; end: Date | null }>(
    { start: null, end: null },
    dateRangesEqual
  );

  // Flags filter state
  const [selectedFlags, setSelectedFlags] = useStableState<Set<FlagFilter>>(new Set(), setsEqual);

  // Sorting state - persist to localStorage
  const [sortField, setSortField] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('adminStudents_sortField') || 'student_last_name';
    }
    return 'student_last_name';
  });
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('adminStudents_sortDirection');
      return saved === 'desc' ? 'desc' : 'asc';
    }
    return 'asc';
  });

  // Pagination state - persist to localStorage
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('adminStudents_itemsPerPage');
      return saved ? parseInt(saved, 10) : initialPageSize;
    }
    return initialPageSize;
  });

  // Persist sort preferences
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('adminStudents_sortField', sortField);
      localStorage.setItem('adminStudents_sortDirection', sortDirection);
    }
  }, [sortField, sortDirection]);

  // Persist pagination preference
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('adminStudents_itemsPerPage', String(itemsPerPage));
    }
  }, [itemsPerPage]);

  const isMountedRef = useRef(true);

  // AbortController for request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);
  // Track request ID to ignore stale responses
  const requestIdRef = useRef(0);
  // Track last cache key to prevent redundant state updates
  const lastCacheKeyRef = useRef<string | null>(null);
  // Ref to hold the latest fetchStudents function (prevents stale closure in intervals)
  const fetchStudentsRef = useRef<(showRefreshing?: boolean, skipCache?: boolean) => Promise<void>>();

  // Toggle helpers
  const toggleStatus = useCallback((status: ApplicationStatus) => {
    setSelectedStatuses((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      return newSet;
    });
  }, []);

  const clearStatusFilters = useCallback(() => {
    setSelectedStatuses(new Set());
  }, []);

  const toggleSchool = useCallback((schoolId: string) => {
    setSelectedSchools((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(schoolId)) {
        newSet.delete(schoolId);
      } else {
        newSet.add(schoolId);
      }
      return newSet;
    });
  }, []);

  const clearSchoolFilters = useCallback(() => {
    setSelectedSchools(new Set());
  }, []);

  const toggleGrade = useCallback((grade: string) => {
    setSelectedGrades((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(grade)) {
        newSet.delete(grade);
      } else {
        newSet.add(grade);
      }
      return newSet;
    });
  }, []);

  const clearGradeFilters = useCallback(() => {
    setSelectedGrades(new Set());
  }, []);

  const toggleCity = useCallback((city: string) => {
    setSelectedCities((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(city)) {
        newSet.delete(city);
      } else {
        newSet.add(city);
      }
      return newSet;
    });
  }, []);

  const clearCityFilters = useCallback(() => {
    setSelectedCities(new Set());
  }, []);

  const toggleSchoolChoice = useCallback((schoolId: string) => {
    setSelectedSchoolChoices((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(schoolId)) {
        newSet.delete(schoolId);
      } else {
        newSet.add(schoolId);
      }
      return newSet;
    });
  }, []);

  const clearSchoolChoiceFilters = useCallback(() => {
    setSelectedSchoolChoices(new Set());
  }, []);

  const clearDateRange = useCallback(() => {
    setDateRange({ start: null, end: null });
  }, []);

  const toggleFlag = useCallback((flag: FlagFilter) => {
    setSelectedFlags((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(flag)) {
        newSet.delete(flag);
      } else {
        newSet.add(flag);
      }
      return newSet;
    });
  }, []);

  const clearFlagFilters = useCallback(() => {
    setSelectedFlags(new Set());
  }, []);

  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setSelectedStatuses(new Set());
    setSelectedSchools(new Set());
    setSelectedGrades(new Set());
    setSelectedCities(new Set());
    setSelectedSchoolChoices(new Set());
    setDateRange({ start: null, end: null });
    setSelectedFlags(new Set());
    setCurrentPage(1);
  }, []);

  // Sort handler
  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
      setCurrentPage(1);
    },
    [sortField]
  );

  // Build RPC params
  const buildParams = useCallback((): GetAdminStudentsParams => {
    // Determine default statuses based on tab
    const defaultStatuses = activeTab === 'all' ? ENROLLED_STATUSES : APPLICATION_STATUSES;

    // Use selected statuses if any, otherwise use defaults
    const statuses =
      selectedStatuses.size > 0 ? Array.from(selectedStatuses) : defaultStatuses;

    const params: GetAdminStudentsParams = {
      p_academic_year: academicYear,
      p_statuses: statuses,
      p_search_type: searchType,
      p_sort_field: sortField,
      p_sort_direction: sortDirection,
      p_limit: itemsPerPage,
      p_offset: (currentPage - 1) * itemsPerPage,
    };

    // School targeting
    if (schoolId === 'all') {
      // When "All Schools" is selected, check if there are school filters
      if (selectedSchools.size > 0) {
        params.p_school_ids = Array.from(selectedSchools);
      }
      // Otherwise, don't set p_school_id or p_school_ids (fetch all)
    } else if (schoolId) {
      params.p_school_id = schoolId;
    }

    // Search
    if (debouncedSearchQuery.trim()) {
      params.p_search_query = debouncedSearchQuery.trim();
    }

    // Grade filter
    if (selectedGrades.size > 0) {
      params.p_grades = Array.from(selectedGrades);
    }

    // City filter
    if (selectedCities.size > 0) {
      params.p_cities = Array.from(selectedCities);
    }

    // School choices filter
    if (selectedSchoolChoices.size > 0) {
      params.p_school_choices = Array.from(selectedSchoolChoices);
    }

    // Date range filter
    if (dateRange.start) {
      params.p_date_start = dateRange.start.toISOString();
    }
    if (dateRange.end) {
      params.p_date_end = dateRange.end.toISOString();
    }

    // Flags filter
    if (selectedFlags.has('sibling')) {
      params.p_has_sibling = true;
    }
    if (selectedFlags.has('staff')) {
      params.p_is_staff = true;
    }
    if (selectedFlags.has('transportation')) {
      params.p_needs_transportation = true;
    }

    return params;
  }, [
    schoolId,
    academicYear,
    activeTab,
    selectedStatuses,
    debouncedSearchQuery,
    searchType,
    selectedSchools,
    selectedGrades,
    selectedCities,
    selectedSchoolChoices,
    dateRange,
    selectedFlags,
    sortField,
    sortDirection,
    currentPage,
    itemsPerPage,
  ]);

  // Cohort-wide per-status counts (server-side, filter-aware). Deliberately ignores the
  // status filter and pagination/sort so every status tab shows its true total within the
  // other active filters — without loading the whole cohort client-side.
  useEffect(() => {
    if (!enabled || !academicYear) return;
    let cancelled = false;
    // This aggregate ignores pagination by design, so it scans the whole cohort
    // matching the search. Superseded ones must be cancelled server-side, not
    // merely ignored client-side.
    const countAbort = new AbortController();
    (async () => {
      const countParams: Record<string, unknown> = {
        p_academic_year: academicYear,
        p_search_type: searchType,
      };
      if (schoolId === 'all') {
        if (selectedSchools.size > 0) countParams.p_school_ids = Array.from(selectedSchools);
      } else if (schoolId) {
        countParams.p_school_id = schoolId;
      }
      if (debouncedSearchQuery.trim()) countParams.p_search_query = debouncedSearchQuery.trim();
      if (selectedGrades.size > 0) countParams.p_grades = Array.from(selectedGrades);
      if (selectedCities.size > 0) countParams.p_cities = Array.from(selectedCities);
      if (selectedSchoolChoices.size > 0) countParams.p_school_choices = Array.from(selectedSchoolChoices);
      if (dateRange.start) countParams.p_date_start = dateRange.start.toISOString();
      if (dateRange.end) countParams.p_date_end = dateRange.end.toISOString();
      if (selectedFlags.has('sibling')) countParams.p_has_sibling = true;
      if (selectedFlags.has('staff')) countParams.p_is_staff = true;
      if (selectedFlags.has('transportation')) countParams.p_needs_transportation = true;

      const countCacheKey = JSON.stringify(countParams);
      const cachedCounts = getCachedStatusCounts(countCacheKey);
      if (cachedCounts) {
        if (!cancelled) setStatusCounts(cachedCounts);
        return;
      }

      try {
        const { data, error } = await supabase
          .rpc('get_admin_student_status_counts', countParams)
          .abortSignal(countAbort.signal);
        if (cancelled) return;
        if (error || !data) {
          // Leaving the previous counts up would show another year's or filter's numbers.
          console.error('Error fetching admin status counts:', error);
          setStatusCounts({});
          return;
        }
        const counts: Record<string, number> = {};
        for (const row of data as Array<{ status: string; count: number }>) {
          if (row?.status) counts[row.status] = Number(row.count) || 0;
        }
        setCachedStatusCounts(countCacheKey, counts);
        if (!cancelled) setStatusCounts(counts);
      } catch (err) {
        // An aborted request rejects. Without this try/catch that surfaced as an
        // unhandled promise rejection once the signal was actually attached.
        if (err instanceof Error && err.name === 'AbortError') return;
        console.error('Error fetching admin status counts:', err);
        if (!cancelled) setStatusCounts({});
      }
    })();
    return () => { cancelled = true; countAbort.abort(); };
  }, [enabled, schoolId, academicYear, searchType, debouncedSearchQuery, selectedSchools, selectedGrades, selectedCities, selectedSchoolChoices, dateRange.start, dateRange.end, selectedFlags, countRefreshToken]);

  // Prefetch next page silently in background
  const prefetchNextPage = useCallback(
    async (currentTotalCount: number) => {
      if (!enabled || !academicYear) return;

      // Calculate if there's a next page
      const totalPages = Math.ceil(currentTotalCount / itemsPerPage);
      if (currentPage >= totalPages) return; // No next page

      // Build params for next page
      const nextPageParams = {
        ...buildParams(),
        p_offset: currentPage * itemsPerPage, // Next page offset
      };
      const cacheKey = createCacheKey(nextPageParams);

      // Skip if already cached
      if (getCachedResponse(cacheKey)) return;

      try {
        const { data, error: rpcError } = await supabase.rpc('get_admin_students', nextPageParams);

        if (rpcError || !isMountedRef.current) return;

        const rows = (data || []) as AdminStudentRow[];
        const transformed = rows.map(transformAdminStudentRow);
        const count = rows.length > 0 ? rows[0].total_count : 0;

        // Store in cache silently
        setCachedResponse(cacheKey, transformed, count);
      } catch {
        // Silently ignore prefetch errors
      }
    },
    [enabled, academicYear, currentPage, itemsPerPage, buildParams]
  );

  // Fetch students with request cancellation and caching
  const fetchStudents = useCallback(
    async (showRefreshing = false, skipCache = false) => {
      if (!enabled || !academicYear) {
        return;
      }

      // Cancel any in-flight request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      // Create new AbortController for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Increment request ID to track this specific request
      const currentRequestId = ++requestIdRef.current;

      const params = buildParams();
      const cacheKey = createCacheKey(params);

      // Check cache first (unless explicitly skipping cache for refresh)
      let shouldRevalidate = true;
      if (!skipCache) {
        const cached = getCachedResponse(cacheKey);
        if (cached) {
          // Only update state if cache key changed (prevents redundant renders)
          if (lastCacheKeyRef.current !== cacheKey) {
            lastCacheKeyRef.current = cacheKey;
            setStudents(cached.entry.data);
            setTotalCount(cached.entry.totalCount);
          }

          // Still prefetch next page even for cached results
          prefetchNextPage(cached.entry.totalCount);

          // If cache is fresh, don't revalidate. A request this one superseded will not
          // clear the loading flags itself (its requestId is stale), so clear them here.
          if (cached.status === 'fresh') {
            setIsLoading(false);
            setIsRefreshing(false);
            setError(null);
            return;
          }

          // Cache is stale - serve it but revalidate in background (SWR pattern)
          // Don't show loading indicators for background revalidation
          shouldRevalidate = true;
        }
      }

      // No cache hit or stale cache - fetch from server
      // Only show loading state if we don't have stale data to show
      if (!getCachedResponse(cacheKey)) {
        if (showRefreshing) {
          setIsRefreshing(true);
        } else {
          setIsLoading(true);
        }
      }
      setError(null);

      try {
        // The signal must be handed to PostgREST. It previously existed only as
        // a local AbortController that was aborted but never attached, so every
        // superseded search still ran to completion inside Postgres and competed
        // for the same connection pool as the query the user was waiting on.
        const { data, error: rpcError } = await supabase
          .rpc('get_admin_students', params)
          .abortSignal(abortController.signal);

        // Check if request was cancelled or a newer request was started
        if (abortController.signal.aborted || currentRequestId !== requestIdRef.current) {
          return;
        }

        if (rpcError) {
          throw rpcError;
        }

        if (isMountedRef.current) {
          const rows = (data || []) as AdminStudentRow[];
          const transformed = rows.map(transformAdminStudentRow);
          const count = rows.length > 0 ? rows[0].total_count : 0;

          // A page past the end (rows were deleted, or the list shrank) comes back empty and
          // carries no total. Go back to the first page rather than show "no students".
          if (rows.length === 0 && currentPage > 1) {
            setCurrentPage(1);
            return;
          }

          // Update cache
          setCachedResponse(cacheKey, transformed, count);

          // Track cache key to prevent redundant state updates
          lastCacheKeyRef.current = cacheKey;

          // Update state
          setStudents(transformed);
          setTotalCount(count);

          // Prefetch next page in background (don't await)
          prefetchNextPage(count);
        }
      } catch (err: unknown) {
        // Ignore abort errors
        if (err instanceof Error && err.name === 'AbortError') {
          return;
        }

        console.error('Error fetching admin students:', err);
        EnhancedErrorLogger.getInstance().logQuickError(toError(err), 'AdminStudents', { context: 'fetch students' });
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          const errorMessage =
            err instanceof Error ? err.message : 'Failed to load students';
          setError(errorMessage);
          // The rows on screen belong to a different year, school, filter or page.
          // Showing them under this request's error would pass them off as its answer.
          if (lastCacheKeyRef.current !== cacheKey) {
            lastCacheKeyRef.current = null;
            setStudents([]);
            setTotalCount(0);
          }
        }
      } finally {
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
      }
    },
    [enabled, academicYear, buildParams, prefetchNextPage, currentPage]
  );

  // Keep fetchStudents ref updated (for use in intervals without causing recreations)
  fetchStudentsRef.current = fetchStudents;

  // Refresh function - skips cache to get fresh data
  const refresh = useCallback(async () => {
    // Counts are a separate RPC with its own cache; a refresh must invalidate
    // both, then force the count effect to re-run.
    clearStatusCountCache();
    setCountRefreshToken((t) => t + 1);
    await fetchStudents(true, true); // showRefreshing=true, skipCache=true
  }, [fetchStudents]);

  // Export all matching students (fetches in chunks, bypasses pagination)
  const exportAllStudents = useCallback(
    async (onProgress?: (current: number, total: number) => void): Promise<TransformedAdminStudent[]> => {
      if (!enabled || !academicYear) {
        return [];
      }

      setIsExporting(true);
      setExportProgress({ current: 0, total: totalCount });

      const EXPORT_CHUNK_SIZE = 500; // Larger chunks for export efficiency
      const allStudents: TransformedAdminStudent[] = [];
      let offset = 0;
      let fetchedTotal = 0;

      try {
        // Build base params without pagination
        const baseParams = buildParams();

        while (true) {
          const chunkParams = {
            ...baseParams,
            p_limit: EXPORT_CHUNK_SIZE,
            p_offset: offset,
          };

          const { data, error: rpcError } = await supabase.rpc('get_admin_students', chunkParams);

          if (rpcError) {
            throw rpcError;
          }

          const rows = (data || []) as AdminStudentRow[];
          if (rows.length === 0) break;

          const transformed = rows.map(transformAdminStudentRow);
          allStudents.push(...transformed);

          // Get total from first row if we don't have it
          if (fetchedTotal === 0 && rows.length > 0) {
            fetchedTotal = rows[0].total_count;
            setExportProgress({ current: allStudents.length, total: fetchedTotal });
          }

          // Update progress
          const progress = { current: allStudents.length, total: fetchedTotal };
          setExportProgress(progress);
          onProgress?.(progress.current, progress.total);

          // Check if we've fetched all
          if (rows.length < EXPORT_CHUNK_SIZE || allStudents.length >= fetchedTotal) {
            break;
          }

          offset += EXPORT_CHUNK_SIZE;

          // Small delay to prevent overwhelming the server
          await new Promise(resolve => setTimeout(resolve, 50));
        }

        return allStudents;
      } catch (err) {
        console.error('Error exporting students:', err);
        EnhancedErrorLogger.getInstance().logQuickError(toError(err), 'AdminStudents', { context: 'update student' });
        throw err;
      } finally {
        setIsExporting(false);
        setExportProgress({ current: 0, total: 0 });
      }
    },
    [enabled, academicYear, totalCount, buildParams]
  );

  // Auto-fetch on dependency changes
  useEffect(() => {
    fetchStudents();
  }, [
    // `enabled` belongs here for the same reason the status-counts effect lists
    // it: gating on tab visibility means this must refetch when the panel comes
    // back. Without it the counts resynced on return but the table did not, so
    // the admin saw fresh count badges over an arbitrarily stale roster.
    // fetchStudents itself early-returns while disabled, so the false direction
    // is a no-op.
    enabled,
    schoolId,
    academicYear,
    activeTab,
    debouncedSearchQuery,
    searchType,
    selectedStatuses,
    selectedSchools,
    selectedGrades,
    selectedCities,
    selectedSchoolChoices,
    dateRange,
    selectedFlags,
    sortField,
    sortDirection,
    currentPage,
    itemsPerPage,
  ]);

  // Reset page when filters change (but not on sort change)
  useEffect(() => {
    setCurrentPage(1);
  }, [
    debouncedSearchQuery,
    searchType,
    selectedStatuses,
    selectedSchools,
    selectedGrades,
    selectedCities,
    selectedSchoolChoices,
    dateRange,
    selectedFlags,
    activeTab,
  ]);

  // Reset filters when tab changes
  useEffect(() => {
    setSelectedStatuses(new Set());
    setCurrentPage(1);
  }, [activeTab]);

  // Cleanup
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      // Cancel any in-flight request when unmounting
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Background auto-refresh when tab is visible
  // Uses ref to avoid recreating interval when fetchStudents changes
  useEffect(() => {
    if (!enabled) return;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const startBackgroundRefresh = () => {
      // Clear existing interval
      if (intervalId) clearInterval(intervalId);

      intervalId = setInterval(async () => {
        // Only refresh if page is visible and not already loading
        if (document.hidden || isLoading || isRefreshing || isExporting) return;

        setIsBackgroundRefreshing(true);
        try {
          // Force skip cache to get fresh data - use ref to get latest function
          await fetchStudentsRef.current?.(false, true);
        } finally {
          if (isMountedRef.current) {
            setIsBackgroundRefreshing(false);
          }
        }
      }, BACKGROUND_REFRESH_INTERVAL);
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab hidden - clear interval to save resources
        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } else {
        // Tab visible again - restart background refresh
        startBackgroundRefresh();
      }
    };

    // Start background refresh
    startBackgroundRefresh();

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, isLoading, isRefreshing, isExporting]); // Removed fetchStudents - using ref instead

  // Calculate active filter count
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (selectedStatuses.size > 0) count++;
    if (selectedSchools.size > 0) count++;
    if (selectedGrades.size > 0) count++;
    if (selectedCities.size > 0) count++;
    if (selectedSchoolChoices.size > 0) count++;
    if (dateRange.start || dateRange.end) count++;
    if (selectedFlags.size > 0) count++;
    if (debouncedSearchQuery.trim()) count++;
    return count;
  }, [
    selectedStatuses,
    selectedSchools,
    selectedGrades,
    selectedCities,
    selectedSchoolChoices,
    dateRange,
    selectedFlags,
    debouncedSearchQuery,
  ]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  // Memoize return value to prevent unnecessary re-renders of consumers
  return useMemo(() => ({
    // Data
    students,
    totalCount,
    statusCounts,

    // Loading states
    isLoading,
    isRefreshing,
    isBackgroundRefreshing,
    error,

    // Search
    searchQuery,
    setSearchQuery,
    searchType,
    setSearchType,

    // Status filter
    selectedStatuses,
    setSelectedStatuses,
    toggleStatus,
    clearStatusFilters,

    // Schools filter
    selectedSchools,
    setSelectedSchools,
    toggleSchool,
    clearSchoolFilters,

    // Grade filter
    selectedGrades,
    setSelectedGrades,
    toggleGrade,
    clearGradeFilters,

    // City filter
    selectedCities,
    setSelectedCities,
    toggleCity,
    clearCityFilters,

    // School choices filter
    selectedSchoolChoices,
    setSelectedSchoolChoices,
    toggleSchoolChoice,
    clearSchoolChoiceFilters,

    // Date range filter
    dateRange,
    setDateRange,
    clearDateRange,

    // Flags filter
    selectedFlags,
    setSelectedFlags,
    toggleFlag,
    clearFlagFilters,

    // Sorting
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    handleSort,

    // Pagination
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalPages,

    // Actions
    refresh,
    fetchStudents,
    clearAllFilters,
    clearCache,

    // Export
    isExporting,
    exportProgress,
    exportAllStudents,

    // Filter counts
    activeFilterCount,
  }), [
    students, totalCount, statusCounts,
    isLoading, isRefreshing, isBackgroundRefreshing, error,
    isExporting, exportProgress, exportAllStudents,
    searchQuery, setSearchQuery, searchType, setSearchType,
    selectedStatuses, setSelectedStatuses, toggleStatus, clearStatusFilters,
    selectedSchools, setSelectedSchools, toggleSchool, clearSchoolFilters,
    selectedGrades, setSelectedGrades, toggleGrade, clearGradeFilters,
    selectedCities, setSelectedCities, toggleCity, clearCityFilters,
    selectedSchoolChoices, setSelectedSchoolChoices, toggleSchoolChoice, clearSchoolChoiceFilters,
    dateRange, setDateRange, clearDateRange,
    selectedFlags, setSelectedFlags, toggleFlag, clearFlagFilters,
    sortField, setSortField, sortDirection, setSortDirection, handleSort,
    currentPage, setCurrentPage, itemsPerPage, setItemsPerPage, totalPages,
    refresh, fetchStudents, clearAllFilters, clearCache,
    activeFilterCount,
  ]);
}

export default useAdminStudents;
